import { useState, useEffect } from 'react';
import { 
  ComposableMap, 
  Geographies, 
  Geography, 
  ZoomableGroup 
} from 'react-simple-maps';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer 
} from 'recharts';
import { Link } from 'react-router-dom';

import { SignInModal } from '../components/SignInModal';
import logoImage from '../images/MicroFeedback_Text_Logo.png';
import { PRICING_PLANS } from '../config/pricing';

// Sample data for visualizations
const sampleAnalyticsData = [
  { date: 'Mon', votes: 45, positive: 38, negative: 7 },
  { date: 'Tue', votes: 52, positive: 44, negative: 8 },
  { date: 'Wed', votes: 38, positive: 32, negative: 6 },
  { date: 'Thu', votes: 67, positive: 58, negative: 9 },
  { date: 'Fri', votes: 73, positive: 65, negative: 8 },
  { date: 'Sat', votes: 41, positive: 36, negative: 5 },
  { date: 'Sun', votes: 29, positive: 25, negative: 4 },
];

const sampleCountries = {
  'United States': 156,
  'United Kingdom': 89,
  'Canada': 67,
  'Germany': 45,
  'Australia': 34,
  'France': 28,
  'Japan': 23,
  'Netherlands': 19,
};

export function LandingPage() {
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [stats, setStats] = useState({
    totalVotes: 0,
    activeSites: 0,
    countries: 0,
    satisfaction: 0,
  });

  useEffect(() => {
    // Animate statistics
    const animateStats = () => {
      const targetStats = {
        totalVotes: 15420,
        activeSites: 847,
        countries: 32,
        satisfaction: 89,
      };

      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        
        setStats({
          totalVotes: Math.floor(targetStats.totalVotes * progress),
          activeSites: Math.floor(targetStats.activeSites * progress),
          countries: Math.floor(targetStats.countries * progress),
          satisfaction: Math.floor(targetStats.satisfaction * progress),
        });

        if (currentStep >= steps) {
          clearInterval(interval);
        }
      }, stepDuration);
    };

    setTimeout(animateStats, 500);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
              <img 
                src={logoImage} 
                alt="MicroFeedback" 
                className="h-8 w-auto"
              />
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <a
                href="/demo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-slate-900 transition-colors"
              >
                Demo
              </a>
              <button 
                onClick={() => setIsSignInModalOpen(true)}
                className="text-slate-600 hover:text-slate-900 transition-colors"
              >
                Sign in
              </button>
              <button 
                onClick={() => setIsSignInModalOpen(true)}
                className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 transition-colors"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Understand your users
              <br />
              <span className="text-blue-600">in real-time</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
              Collect instant feedback from your website visitors with a simple, 
              lightweight widget. Get actionable insights to improve your content and user experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsSignInModalOpen(true)}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Start collecting feedback
              </button>
            </div>
          </div>

          {/* World Map Visualization */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-lg font-semibold text-slate-900 mb-6 text-center">
                Global feedback collection
              </h3>
              <ComposableMap
                projection="geoNaturalEarth1"
                projectionConfig={{
                  scale: 140,
                  center: [0, 20]
                }}
                style={{
                  width: '100%',
                  height: '400px'
                }}
              >
                <ZoomableGroup zoom={1} center={[0, 20]}>
                  <Geographies geography="https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json">
                    {({ geographies }) =>
                      geographies.map((geo) => {
                        const countryName = geo.properties.name as string;
                        const voteCount = sampleCountries[countryName as keyof typeof sampleCountries] || 0;
                        const intensity = voteCount > 0 ? Math.min(voteCount / 156, 1) : 0;
                        
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={voteCount > 0 ? `rgba(59, 130, 246, ${0.2 + intensity * 0.6})` : '#f1f5f9'}
                            stroke={voteCount > 0 ? '#1e40af' : '#e2e8f0'}
                            strokeWidth={voteCount > 0 ? 1 : 0.5}
                            style={{
                              default: { outline: 'none' },
                              hover: { outline: 'none', fill: voteCount > 0 ? `rgba(59, 130, 246, ${0.4 + intensity * 0.6})` : '#e2e8f0' },
                              pressed: { outline: 'none' },
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>
                </ZoomableGroup>
              </ComposableMap>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                {stats.totalVotes.toLocaleString()}
              </div>
              <div className="text-slate-600">Feedback collected</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                {stats.activeSites}
              </div>
              <div className="text-slate-600">Active websites</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                {stats.countries}
              </div>
              <div className="text-slate-600">Countries reached</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                {stats.satisfaction}%
              </div>
              <div className="text-slate-600">Satisfaction rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Everything you need to understand your users
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From simple feedback collection to advanced analytics, 
              MicroFeedback provides the insights you need to make data-driven decisions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                One-click feedback
              </h3>
              <p className="text-slate-600 mb-6">
                Add a lightweight feedback widget to any page in seconds. 
                Users can vote with a single click and optionally leave comments.
              </p>
              <div className="flex items-center text-sm text-slate-500">
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                  Under 3KB
                </span>
                <span className="ml-2">Lightweight widget</span>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Real-time analytics
              </h3>
              <p className="text-slate-600 mb-6">
                View live feedback trends, geographic distribution, and page performance. 
                Data updates every 5 minutes for instant insights.
              </p>
              <div className="flex items-center text-sm text-slate-500">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                  Live data
                </span>
                <span className="ml-2">Updated every 5 minutes</span>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4 19h6v-2H4v2zM4 15h10v-2H4v2zM4 11h14v-2H4v2zM4 7h18v-2H4v2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Smart alerts
              </h3>
              <p className="text-slate-600 mb-6">
                Get notified when negative feedback exceeds your threshold. 
                Stay on top of user sentiment and address issues quickly.
              </p>
              <div className="flex items-center text-sm text-slate-500">
                <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
                  Customizable
                </span>
                <span className="ml-2">Set your own thresholds</span>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Multi-site management
              </h3>
              <p className="text-slate-600 mb-6">
                Manage feedback from multiple websites in one dashboard. 
                Compare performance across different sites and pages.
              </p>
              <div className="flex items-center text-sm text-slate-500">
                <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs font-medium">
                  Unlimited
                </span>
                <span className="ml-2">Sites per account</span>
              </div>
            </div>

            {/* Feature 5 - Enhanced Privacy */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Privacy & security
              </h3>
              <p className="text-slate-600 mb-6">
                Enterprise-grade security with IP hashing, encrypted data, and one-click GDPR deletion. 
                No personal data collected from feedback users.
              </p>
              <div className="flex flex-wrap gap-2 text-sm text-slate-500">
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                  GDPR Compliant
                </span>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                  IP Hashing
                </span>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                  Auto Deletion
                </span>
              </div>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Export & integrate
              </h3>
              <p className="text-slate-600 mb-6">
                Export your data as CSV or integrate with your existing tools via API. 
                Connect with your workflow seamlessly.
              </p>
              <div className="flex items-center text-sm text-slate-500">
                <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium">
                  API Access
                </span>
                <span className="ml-2">Full integration support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy & Security Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">
              Privacy-first feedback collection
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Built with enterprise-grade security and GDPR compliance from day one. 
              Your users&apos; privacy is protected at every step.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* No Personal Data */}
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                No Personal Data
              </h3>
              <p className="text-slate-300 text-sm">
                Zero emails, names, or personal information collected from feedback users.
              </p>
            </div>

            {/* IP Hashing */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                SHA-256 IP Hashing
              </h3>
              <p className="text-slate-300 text-sm">
                IP addresses are immediately hashed with salt. Original IPs never stored.
              </p>
            </div>

            {/* One-Click Deletion */}
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                One-Click Deletion
              </h3>
              <p className="text-slate-300 text-sm">
                Complete GDPR deletion of all data with automated audit logging.
              </p>
            </div>

            {/* Data Export */}
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Full Data Export
              </h3>
              <p className="text-slate-300 text-sm">
                Export all your data as CSV anytime. Up to 100K records per export.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              href="/privacy"
              className="inline-flex items-center text-white bg-slate-800 hover:bg-slate-700 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Read our detailed Privacy Policy
            </a>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Start free and scale as you grow. No hidden fees, no surprises.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 relative">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{PRICING_PLANS.free.name}</h3>
                <div className="text-4xl font-bold text-slate-900 mb-2">{PRICING_PLANS.free.displayPrice.monthly}</div>
                <div className="text-slate-600 mb-6">{PRICING_PLANS.free.tagline}</div>
                
                <ul className="space-y-4 mb-8 text-left">
                  {PRICING_PLANS.free.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature.name}
                    </li>
                  ))}
                </ul>
                
                <button 
                  onClick={() => setIsSignInModalOpen(true)}
                  className="w-full bg-slate-900 text-white py-3 px-6 rounded-lg font-semibold hover:bg-slate-800 transition-colors block text-center"
                >
                  Start free trial
                </button>
              </div>
            </div>

            {/* Pro Plan */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-blue-500 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  {PRICING_PLANS.pro.tagline}
                </span>
              </div>
              
              <div className="text-center">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{PRICING_PLANS.pro.name}</h3>
                <div className="text-4xl font-bold text-slate-900 mb-2">{PRICING_PLANS.pro.displayPrice.monthly}</div>
                <div className="text-slate-600 mb-6">per month</div>
                
                <ul className="space-y-4 mb-8 text-left">
                  {PRICING_PLANS.pro.features.slice(0, 6).map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature.name}
                    </li>
                  ))}
                </ul>
                
                <button 
                  onClick={() => setIsSignInModalOpen(true)}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors block text-center"
                >
                  Get started
                </button>
              </div>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 relative">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{PRICING_PLANS.enterprise.name}</h3>
                <div className="text-4xl font-bold text-slate-900 mb-2">{PRICING_PLANS.enterprise.displayPrice.monthly}</div>
                <div className="text-slate-600 mb-6">per month</div>
                
                <ul className="space-y-4 mb-8 text-left">
                  {PRICING_PLANS.enterprise.features.slice(0, 6).map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature.name}
                    </li>
                  ))}
                </ul>
                
                <button 
                  onClick={() => setIsSignInModalOpen(true)}
                  className="w-full bg-slate-900 text-white py-3 px-6 rounded-lg font-semibold hover:bg-slate-800 transition-colors block text-center"
                >
                  Get started
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customers Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              Trusted by growing companies
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Join companies that are already improving their user experience with MicroFeedback.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
              {/* Company 1 */}
              <div className="flex justify-center">
                <a 
                  href="https://weavenwonder.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-600 hover:text-slate-900 transition-colors"
                >
                  <div className="w-48 h-12 rounded-lg flex items-center justify-center px-3">
                    <span 
                      className="font-bold text-sm"
                      style={{
                        fontFamily: "'Fredoka One', cursive",
                        color: '#ffd93d'
                      }}
                    >
                      Weave-n-Wonder
                    </span>
                  </div>
                </a>
              </div>

              {/* Company 2 */}
              <div className="flex justify-center">
                <a 
                  href="https://cognitas.io" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-600 hover:text-slate-900 transition-colors"
                >
                  <svg 
                    width="200" 
                    height="40" 
                    viewBox="0 0 160 40" 
                    className="text-slate-600 hover:text-slate-900 transition-colors"
                    aria-label="Cognitas logo"
                  >
                    <g 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      fill="none" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      {/* Roof */}
                      <path d="M5 15 L25 5 L45 15"></path>
                      {/* Columns */}
                      <path d="M12 15 V30"></path>
                      <path d="M38 15 V30"></path>
                      {/* Base */}
                      <line x1="5" y1="35" x2="45" y2="35"></line>
                      {/* Checkmark */}
                      <path d="M18 26 l5 5 l10 -10"></path>
                    </g>
                    <text 
                      x="60" 
                      y="30" 
                      fontFamily="Inter, sans-serif" 
                      fontSize="24" 
                      fill="currentColor"
                      className="font-semibold"
                    >
                      Cognitas
                    </text>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics Preview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Powerful analytics at your fingertips
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              See exactly how your content performs with detailed insights and visualizations.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Chart */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-6">
                  Feedback trends
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={sampleAnalyticsData}>
                    <defs>
                      <linearGradient id="positiveGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0.05}/>
                      </linearGradient>
                      <linearGradient id="negativeGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#EF4444" stopOpacity={0.05}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6B7280' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
                    <Area
                      type="monotone"
                      dataKey="positive"
                      stroke="#10B981"
                      strokeWidth={2}
                      fill="url(#positiveGradient)"
                      name="Positive"
                    />
                    <Area
                      type="monotone"
                      dataKey="negative"
                      stroke="#EF4444"
                      strokeWidth={2}
                      fill="url(#negativeGradient)"
                      name="Negative"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Stats */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-slate-900">
                  Key metrics
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                    <div>
                      <div className="text-sm text-slate-600">Total feedback</div>
                      <div className="text-2xl font-bold text-slate-900">345</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-green-600 font-medium">+12%</div>
                      <div className="text-xs text-slate-500">vs last week</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                    <div>
                      <div className="text-sm text-slate-600">Satisfaction rate</div>
                      <div className="text-2xl font-bold text-slate-900">89%</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-green-600 font-medium">+3%</div>
                      <div className="text-xs text-slate-500">vs last week</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                    <div>
                      <div className="text-sm text-slate-600">Top performing page</div>
                      <div className="text-lg font-semibold text-slate-900">/pricing</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-blue-600 font-medium">156 votes</div>
                      <div className="text-xs text-slate-500">This week</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to understand your users better?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Join thousands of websites already collecting valuable feedback 
            and improving their user experience with MicroFeedback.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setIsSignInModalOpen(true)}
              className="bg-white text-slate-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-slate-100 transition-colors"
            >
              Start free trial
            </button>
            <button
              className="border border-slate-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:border-slate-500 transition-colors"
              onClick={() => {
                window.alert('To schedule a demo, please email us at hello@microfb.com');
              }}
            >
              Schedule demo
            </button>
          </div>
          <p className="text-sm text-slate-400 mt-6">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-200 mt-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">MicroFeedback</h3>
                <p className="text-slate-600 text-sm">
                  Simple, powerful feedback collection for modern websites.
                </p>
              </div>
              <div>
                {/* <h4 className="font-semibold text-slate-900 mb-4">Product</h4> */}
                <ul className="space-y-2 text-sm text-slate-600">
                  {/* <li><a href="#" className="hover:text-slate-900">Features</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">Pricing</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">Documentation</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">API</a></li> */}
                </ul>
              </div>
              <div>
                {/* <h4 className="font-semibold text-slate-900 mb-4">Company</h4> */}
                <ul className="space-y-2 text-sm text-slate-600">
                  {/* <li><a href="#" className="hover:text-slate-900">About</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">Blog</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">Careers</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">Contact</a></li> */}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-4">Support</h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  {/* <li><a href="#" className="hover:text-slate-900">Help Center</a></li> */}
                  {/* <li><a href="#" className="hover:text-slate-900">Status</a></li> */}
                  <li><a href="/privacy" className="hover:text-slate-900">Privacy</a></li>
                  {/* <li><a href="#" className="hover:text-slate-900">Terms</a></li> */}
                </ul>
              </div>
            </div>
            <div className="border-t border-slate-200 mt-8 pt-8 text-center">
              <p className="text-sm text-slate-500"> 
                © {new Date().getFullYear()} MicroFeedback. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      
      <SignInModal 
        isOpen={isSignInModalOpen}
        onClose={() => setIsSignInModalOpen(false)}
      />
    </div>
  );
} 